import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  type BasicCheckMerchantsResponse,
  type OfferV11,
  type PimProductV11,
  PrescriptionV3PrescriptionTypeEnum,
  type ProductObjectRequest,
} from '@redteclab/api/clients/bully'

import {
  type NowGeneralContextValue,
  useNowApiV4SwrGetBasicCheckMutation,
  useNowGeneralContext,
} from '../../../now'
import { TENANT } from '../../../tenant'
import { priceAccessorGetDisplayPriceInCents } from '../../model/priceAccessor/priceAccessorGetDisplayPriceInCents'
import { productAccessorGetDoesSupportNow } from '../../model/productAccessor/productAccessorGetDoesSupportNow'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'

type NowDataPerProduct = Record<
  string,
  {
    checkedForZipCode: string
    fetchAt: number
    responseData: BasicCheckMerchantsResponse
  } | null
>

export type ProductPageNowDetails = {
  clearNowDataAndZipCode: () => void
  nowBasicCheckMutation: ReturnType<typeof useNowApiV4SwrGetBasicCheckMutation>
  nowCustomerJourneyEnabled: boolean
  nowDataPerProduct: NowDataPerProduct
  nowDataPerProductCurrentVariantResponse?: BasicCheckMerchantsResponse
  savedZipCode?: string
  triggerCheck: (
    product: PimProductV11,
    offer: OfferV11,
    zipCode: string,
  ) => void
}

const triggerCheckForProductMutation = ({
  offer,
  product,
  trigger,
  zipCode,
}: {
  offer: OfferV11
  product: PimProductV11
  trigger: ReturnType<typeof useNowApiV4SwrGetBasicCheckMutation>['trigger']
  zipCode: string
}): void => {
  const priceInCents = priceAccessorGetDisplayPriceInCents(
    offer,
    PrescriptionV3PrescriptionTypeEnum.NoPrescription,
  )

  if (!priceInCents) {
    return
  }

  void trigger({
    products: [
      {
        nowProduct: 0,
        priceCentsGross: priceInCents,
        quantity: 1,
        upid: product.upid,
      },
    ],
    zipCode,
  })
}

type HandleMutationSuccessParams = {
  nowCustomerJourneyEnabled: boolean
  nowGeneralContext: NowGeneralContextValue
  onMutationSuccess?: () => void
  products?: ProductObjectRequest[]
  response: BasicCheckMerchantsResponse
  setNowDataPerProduct: Dispatch<SetStateAction<NowDataPerProduct>>
  zipCode: string
}

const handleMutationSuccess = ({
  nowCustomerJourneyEnabled,
  nowGeneralContext,
  onMutationSuccess,
  products,
  response,
  setNowDataPerProduct,
  zipCode,
}: HandleMutationSuccessParams): void => {
  const upid = products?.[0]?.upid
  if (!upid) {
    return
  }

  setNowDataPerProduct((prev) => ({
    ...prev,
    [upid]: {
      checkedForZipCode: zipCode,
      fetchAt: Date.now(),
      responseData: response,
    },
  }))
  // update general zip support.
  nowGeneralContext.updateZipSupportInfo({
    checkedAt: Date.now(),
    inDeliveryRegion: response.zipCodeInDeliveryRegion,
    zip: zipCode,
  })

  if (nowCustomerJourneyEnabled) {
    onMutationSuccess?.()
  }
}

export const useProductPageNowDetails = ({
  currentOffer,
  currentVariant,
  isMasterPage,
  onMutationSuccess,
  tenant,
}: {
  currentOffer?: OfferV11
  currentVariant: PimProductV11
  isMasterPage: boolean
  onMutationSuccess?: () => void
  tenant: TENANT
}): ProductPageNowDetails => {
  const nowGeneralContext = useNowGeneralContext()
  const [nowDataPerProduct, setNowDataPerProduct] = useState<NowDataPerProduct>(
    {},
  )

  const nowCustomerJourneyEnabled: boolean =
    (!productAccessorIsPrescriptionProduct(currentVariant) &&
      currentOffer &&
      tenant === TENANT.COM) ??
    false

  const nowBasicCheckMutation = useNowApiV4SwrGetBasicCheckMutation({
    onSuccess: (response, zipCode, products) => {
      handleMutationSuccess({
        nowCustomerJourneyEnabled,
        nowGeneralContext,
        onMutationSuccess,
        products,
        response,
        setNowDataPerProduct,
        zipCode,
      })
    },
    swrKeyNonce: `productPage-${currentVariant.upid}`,
  })

  useEffect(() => {
    const zipCode = nowGeneralContext.nowGeneralData.zipSupportInfo?.zip
    if (
      !zipCode ||
      !currentOffer ||
      isMasterPage ||
      nowDataPerProduct[currentVariant.upid]?.checkedForZipCode === zipCode ||
      !productAccessorGetDoesSupportNow(currentOffer)
    ) {
      return
    }

    triggerCheckForProductMutation({
      offer: currentOffer,
      product: currentVariant,
      trigger: nowBasicCheckMutation.trigger,
      zipCode,
    })
  }, [
    currentOffer,
    currentVariant,
    isMasterPage,
    nowBasicCheckMutation.trigger,
    nowDataPerProduct,
    nowGeneralContext.nowGeneralData.zipSupportInfo?.zip,
    nowCustomerJourneyEnabled,
  ])

  return useMemo(() => {
    return {
      clearNowDataAndZipCode: (): void => {
        setNowDataPerProduct({})
        nowGeneralContext.clearZipSupportInfo()
        if (nowCustomerJourneyEnabled) {
          onMutationSuccess?.()
        }
      },
      nowBasicCheckMutation,
      nowCustomerJourneyEnabled,
      nowDataPerProduct,
      nowDataPerProductCurrentVariantResponse:
        nowDataPerProduct[currentVariant.upid]?.responseData,
      savedZipCode: nowGeneralContext.nowGeneralData.zipSupportInfo?.zip,
      triggerCheck: (
        product: PimProductV11,
        offer: OfferV11,
        zipCode: string,
      ): void => {
        triggerCheckForProductMutation({
          offer,
          product,
          trigger: nowBasicCheckMutation.trigger,
          zipCode,
        })
      },
    }
  }, [
    currentVariant.upid,
    nowBasicCheckMutation,
    nowDataPerProduct,
    nowGeneralContext,
    nowCustomerJourneyEnabled,
    onMutationSuccess,
  ])
}
